/**
 * Length units.
 *
 * Products keep the unit the vendor entered them in; the vendor's
 * `unitDisplay` (cm, mm or in) is what new sizes are entered in and what
 * shoppers read. Rendering (seamless textures, grout) is always metric, so
 * sizes go through `toCm` before they reach it. Everything converts through cm
 * with the exact factors (1 in = 2.54 cm) and rounds only at the end. Same
 * rules as pms-api-v2 src/utils/units.ts and ai-visualizer.
 */

export type LengthUnit = 'mm' | 'cm' | 'm' | 'in' | 'ft';
export type DisplayUnit = 'cm' | 'mm' | 'in';
export type UnitSystem = 'metric' | 'imperial';

const CM_PER_UNIT: Record<LengthUnit, number> = { mm: 0.1, cm: 1, m: 100, in: 2.54, ft: 30.48 };
const DECIMALS: Record<LengthUnit, number> = { mm: 1, cm: 2, m: 3, in: 2, ft: 2 };

const ALIASES: Record<string, LengthUnit> = {
  mm: 'mm', cm: 'cm', m: 'm',
  in: 'in', 'in.': 'in', inch: 'in', inches: 'in', '"': 'in', '″': 'in',
  ft: 'ft', 'ft.': 'ft', foot: 'ft', feet: 'ft', "'": 'ft', '′': 'ft',
};

/** The unit a spelling means; missing or unknown reads as cm, as products default to. */
export function normalizeUnit(value: unknown): LengthUnit {
  if (typeof value !== 'string') return 'cm';
  return ALIASES[value.trim().toLowerCase()] ?? 'cm';
}

/** A vendor's display unit: cm, mm or in. Anything else reads as cm. */
export function normalizeDisplayUnit(value: unknown): DisplayUnit {
  const unit = normalizeUnit(value);
  return unit === 'mm' || unit === 'in' ? unit : 'cm';
}

export function unitSystem(unit: unknown): UnitSystem {
  const u = normalizeUnit(unit);
  return u === 'in' || u === 'ft' ? 'imperial' : 'metric';
}

/** Convert a length. Same unit returns the value untouched; otherwise rounded for the target unit. */
export function convertLength(value: number, from: unknown, to: unknown): number {
  const fromUnit = normalizeUnit(from);
  const toUnit = normalizeUnit(to);
  if (fromUnit === toUnit) return value;
  const factor = 10 ** DECIMALS[toUnit];
  return Math.round(((value * CM_PER_UNIT[fromUnit]) / CM_PER_UNIT[toUnit]) * factor) / factor;
}

export interface DisplaySize {
  width: number;
  height: number;
  depth?: number;
  unit: LengthUnit;
}

/** A product's size in the unit to show it in. Null without both dimensions. */
export function sizeIn(
  size: { width?: unknown; height?: unknown; depth?: unknown; unit?: unknown } | null | undefined,
  target: unknown,
): DisplaySize | null {
  const width = Number(size?.width);
  const height = Number(size?.height);
  if (!(width > 0) || !(height > 0)) return null;
  const to = normalizeUnit(target);
  const from = size?.unit;
  const depth = Number(size?.depth);
  return {
    width: convertLength(width, from, to),
    height: convertLength(height, from, to),
    ...(depth > 0 ? { depth: convertLength(depth, from, to) } : {}),
    unit: to,
  };
}

/** A length in cm, unrounded. */
export function toCm(value: number, unit: unknown): number {
  return value * CM_PER_UNIT[normalizeUnit(unit)];
}

/**
 * A tile dimension as the seamless generator wants it: cm, to 0.01. The
 * generator reads plankWidth/plankHeight as cm whatever the product's unit,
 * so an mm or inch size passed as-is renders 10× or 2.54× off.
 */
export function plankCm(value: unknown, unit: unknown): number {
  const n = Number(value);
  if (!(n > 0)) return 0;
  return Math.round(toCm(n, unit) * 100) / 100;
}

/** "30x60", "12x24in", '12x24"', "600 x 600 mm", "60x60x2cm" → dimensions and unit (fallback when none is written). */
export function parseDimensions(
  raw: unknown,
  fallbackUnit: unknown = 'cm',
): { width: number; height: number; depth?: number; unit: LengthUnit } | null {
  const match = String(raw ?? '').trim().match(/^([\d.,]+)\s*[x×]\s*([\d.,]+)(?:\s*[x×]\s*([\d.,]+))?\s*([a-z."″'′]*)$/i);
  if (!match) return null;
  const num = (v?: string) => (v ? parseFloat(v.replace(',', '.')) || 0 : 0);
  const unit = match[4] ? normalizeUnit(match[4]) : normalizeUnit(fallbackUnit);
  const depth = num(match[3]);
  return { width: num(match[1]), height: num(match[2]), ...(depth ? { depth } : {}), unit };
}
